import {
  buildPreviewConstraints, resolveSelectedDevice, toDeviceOptions, type DeviceOption, type MediaDevicePreferences
} from "../../services/mediaDevicePreferences";

// Preview lifecycle behind <DeviceReadiness>, kept free of React and the DOM so every lifecycle
// path is unit-tested with fake streams in the node test environment. The component binds it
// 1:1: `useEffect(() => session.acquire(selection), [camera, mic, retry])` — so the returned
// function IS the effect cleanup, and StrictMode's mount → cleanup → mount, device-default
// re-acquisition and unmount-while-pending are exactly the call sequences the tests replay.
//
// Opt-in auto-continue (guest invitation flow only): once the SETTLED selection's preview has a
// live, enabled, unmuted video track AND audio track, the session stops the preview and calls
// onAutoContinue exactly once for its lifetime (one session per mounted readiness stage).
// Denied permission / missing device (getUserMedia rejects), a dead track (ended) or a missing
// track never auto-continue: they surface as errors with the existing "Try again".

export type PreviewHealth = "healthy" | "waiting" | "unusable";

/** Thrown into `failed` when a preview track ended or a kind is missing (the device stopped). */
export class PreviewTrackEndedError extends Error {
  constructor() {
    super("preview track ended");
    this.name = "PreviewTrackEndedError";
  }
}

type TrackLike = Pick<MediaStreamTrack, "enabled" | "muted" | "readyState" | "stop" | "addEventListener" | "removeEventListener">;
type StreamLike = { getTracks(): TrackLike[]; getVideoTracks(): TrackLike[]; getAudioTracks(): TrackLike[] };

/** Healthy = a live, enabled, unmuted video track AND audio track. */
export function previewHealth(stream: StreamLike): PreviewHealth {
  const video = stream.getVideoTracks()[0];
  const audio = stream.getAudioTracks()[0];
  if (!video || !audio || video.readyState !== "live" || audio.readyState !== "live") return "unusable";
  if (!video.enabled || !audio.enabled || video.muted || audio.muted) return "waiting";
  return "healthy";
}

export interface ReadinessMediaApi {
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  enumerateDevices(): Promise<MediaDeviceInfo[]>;
}

/** State sink — the component's setters. Never called after the acquisition that produced it was cleaned up. */
export interface ReadinessSink {
  acquiring(): void;
  stream(stream: MediaStream): void;
  devices(cameras: DeviceOption[], mics: DeviceOption[], cameraId: string | undefined, micId: string | undefined): void;
  ready(): void;
  failed(error: unknown): void;
  health(health: PreviewHealth): void;
}

export interface ReadinessSessionOptions {
  /** Read at the moment a preview is proven, so it can follow a prop. */
  autoContinue: () => boolean;
  onAutoContinue: (prefs: MediaDevicePreferences) => void;
  deviceLabel: (kind: "videoinput" | "audioinput", index: number) => string;
}

export interface DeviceSelection {
  cameraId: string | undefined;
  micId: string | undefined;
}

const TRACK_EVENTS = ["ended", "mute", "unmute"] as const;

export class ReadinessSession {
  private current: MediaStream | null = null;
  private fired = false;
  private unwatch: (() => void) | null = null;
  private evaluate: (() => void) | null = null;

  constructor(private readonly media: ReadinessMediaApi, private readonly sink: ReadinessSink, private readonly options: ReadinessSessionOptions) {}

  /** True once auto-continue has fired (never again for this session). */
  get autoContinued(): boolean {
    return this.fired;
  }

  /** Start (or restart) the preview for a selection. Returns the cleanup (stops tracks, drops late results). */
  acquire(selection: DeviceSelection): () => void {
    let cancelled = false;
    let owned: MediaStream | null = null; // the preview THIS acquisition installed
    this.sink.acquiring();
    void (async () => {
      try {
        const stream = await this.media.getUserMedia(
          buildPreviewConstraints({ cameraDeviceId: selection.cameraId, micDeviceId: selection.micId })
        );
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop()); // late result of a cleaned-up acquisition
          return;
        }
        this.stopStream();
        this.current = stream;
        owned = stream;
        this.sink.stream(stream);

        const devices = await this.media.enumerateDevices();
        if (cancelled) return;
        const cameras = toDeviceOptions(devices, "videoinput", (i) => this.options.deviceLabel("videoinput", i));
        const mics = toDeviceOptions(devices, "audioinput", (i) => this.options.deviceLabel("audioinput", i));
        const cameraId = resolveSelectedDevice(selection.cameraId, cameras);
        const micId = resolveSelectedDevice(selection.micId, mics);
        this.sink.devices(cameras, mics, cameraId, micId);
        this.sink.ready();

        // A changed selection (first load picks concrete default devices) re-acquires right away,
        // so only a settled selection may auto-continue — never the stream about to be replaced.
        const settled = cameraId === selection.cameraId && micId === selection.micId;
        if (settled && !this.fired && this.options.autoContinue()) this.watch(stream, selection, () => cancelled);
      } catch (e) {
        if (cancelled) return; // stale: its cleanup already ran; never touch a newer preview
        // e.g. enumerateDevices rejected after access was granted: release this acquisition's
        // preview (camera light off, no watchers) before showing the error — only if still ours.
        if (owned && this.current === owned) this.release();
        this.sink.failed(e);
      }
    })();
    return () => {
      cancelled = true;
      this.stopWatching();
      this.stopStream();
    };
  }

  /** Re-check health after a camera/mic toggle (auto mode only; no-op otherwise). */
  reevaluate(): void {
    this.evaluate?.();
  }

  /** Manual continue (legacy button): release the preview before the call takes the devices. */
  release(): void {
    this.stopWatching();
    this.stopStream();
  }

  private watch(stream: MediaStream, selection: DeviceSelection, isCancelled: () => boolean): void {
    const tracks = stream.getTracks();
    const evaluate = () => {
      if (isCancelled() || this.fired || this.current !== stream) return;
      const health = previewHealth(stream);
      this.sink.health(health);
      if (health === "healthy") {
        this.fired = true;
        this.release();
        this.options.onAutoContinue({ cameraDeviceId: selection.cameraId, micDeviceId: selection.micId, cameraEnabled: true, micEnabled: true });
      } else if (health === "unusable") {
        this.release();
        this.sink.failed(new PreviewTrackEndedError());
      }
      // "waiting": muted / switched off — the track events or a toggle re-evaluate.
    };
    for (const track of tracks) for (const type of TRACK_EVENTS) track.addEventListener(type, evaluate);
    this.unwatch = () => {
      for (const track of tracks) for (const type of TRACK_EVENTS) track.removeEventListener(type, evaluate);
    };
    this.evaluate = evaluate;
    evaluate();
  }

  private stopWatching(): void {
    this.unwatch?.();
    this.unwatch = null;
    this.evaluate = null;
  }

  private stopStream(): void {
    this.current?.getTracks().forEach((t) => t.stop());
    this.current = null;
  }
}
